// /web/src/consent/ConsentContext.jsx
import React, {createContext, useContext, useEffect, useMemo, useState} from "react";

const LS_KEY = "pebbledash_cookies";
const LEGACY_KEYS = ["pebbledash.consent.v1"];
const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;

const defaultPrefs = {
  essential: true,           // always on
  analytics: false,
  marketing: false,
  updatedAt: 0,
};

const ConsentCtx = createContext({
  prefs: defaultPrefs,
  setPrefs: (_p) => {},
  showSettings: () => {},
  hideSettings: () => {},
  settingsOpen: false,
});

export function ConsentProvider({children}) {
  const [prefs, setPrefsState] = useState(defaultPrefs);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // load once
  useEffect(() => {
    try {
      const keys = [LS_KEY, ...LEGACY_KEYS];
      for (const key of keys) {
        const raw = localStorage.getItem(key);
        if (!raw) continue;
        const saved = JSON.parse(raw);
        if (saved && typeof saved === "object") {
          setPrefsState({ ...defaultPrefs, ...saved });
          if (key !== LS_KEY) {
            localStorage.removeItem(key);
            localStorage.setItem(LS_KEY, JSON.stringify({ ...defaultPrefs, ...saved }));
          }
          break;
        }
      }
    } catch {}
  }, []);

  const setPrefs = (next) => {
    const merged = {
      ...prefs,
      ...next,
      essential: true,
      updatedAt: Date.now(),
    };
    setPrefsState(merged);
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(merged));
      LEGACY_KEYS.forEach((key) => localStorage.removeItem(key));
    } catch {}
  };

  // helper for Step 12: call in your analytics init
  const allow = (key) => !!prefs[key];
  const approvedRecently = () => Date.now() - (prefs.updatedAt || 0) < ONE_YEAR_MS;

  const value = useMemo(() => ({
    prefs, setPrefs, allow, approvedRecently,
    settingsOpen,
    showSettings: () => setSettingsOpen(true),
    hideSettings: () => setSettingsOpen(false),
  }), [prefs, settingsOpen]);

  return <ConsentCtx.Provider value={value}>{children}</ConsentCtx.Provider>;
}

export function useConsent() {
  return useContext(ConsentCtx);
}
